import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, LayoutGrid, Settings, BarChart3 } from 'lucide-react';
import { Card, Button, useToast } from '@caruso/ui';
import { boardsService, Board } from '@/services/boards.service';

export function BoardsPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: boards, isLoading } = useQuery({
    queryKey: ['boards'],
    queryFn: () => boardsService.getAll({ includeSteps: true }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => boardsService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['boards'] });
      toast({
        title: 'Quadro excluido!',
        variant: 'success',
      });
    },
    onError: () => {
      toast({
        title: 'Erro ao excluir quadro',
        variant: 'destructive',
      });
    },
  });

  const handleDelete = (id: string, name: string) => {
    if (confirm(`Tem certeza que deseja excluir o quadro "${name}"? Esta acao nao pode ser desfeita.`)) {
      deleteMutation.mutate(id);
    }
  };

  const getTotalExecutions = (board: Board) => {
    if (!board.steps) return 0;
    return board.steps.reduce((acc, step) => acc + (step._count?.executions || 0), 0);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-white">Quadros</h1>
          <p className="text-brand-ivory/80 mt-2 text-lg">
            Gerencie seus quadros Kanban para acompanhar clientes nos fluxos
          </p>
        </div>
        <Button onClick={() => navigate('/app/quadros/novo')} className="gap-2">
          <Plus className="w-5 h-5" />
          Novo Quadro
        </Button>
      </div>

      {/* Boards List */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <Card key={i} className="p-6 bg-brand-smoke border-brand-gold/20 animate-pulse">
              <div className="h-32 bg-brand-ink/50 rounded"></div>
            </Card>
          ))}
        </div>
      ) : boards && boards.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {boards.map((board) => (
            <Card
              key={board.id}
              className="p-6 bg-brand-smoke border-brand-gold/20 hover:border-brand-gold/40 transition-all cursor-pointer group"
              onClick={() => navigate(`/app/quadros/${board.id}`)}
            >
              <div className="flex items-start justify-between mb-4">
                <div
                  className="w-4 h-4 rounded-full"
                  style={{ backgroundColor: board.color || '#6366f1' }}
                />
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/app/quadros/${board.id}/editar`);
                    }}
                    title="Configurar"
                    className="h-8 w-8"
                  >
                    <Settings className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(board.id, board.name);
                    }}
                    disabled={deleteMutation.isPending}
                    title="Excluir"
                    className="h-8 w-8 text-red-400 hover:bg-red-400/10"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <h3 className="text-xl font-bold text-white mb-2">{board.name}</h3>

              {board.description && (
                <p className="text-brand-ivory/70 text-sm mb-4 line-clamp-2">
                  {board.description}
                </p>
              )}

              <div className="flex items-center gap-4 text-sm text-brand-ivory/60">
                <div className="flex items-center gap-1">
                  <LayoutGrid className="w-4 h-4" />
                  <span>{board.steps?.length || 0} passos</span>
                </div>
                <div className="flex items-center gap-1">
                  <BarChart3 className="w-4 h-4" />
                  <span>{getTotalExecutions(board)} cards</span>
                </div>
              </div>

              {board.steps && board.steps.length > 0 && (
                <div className="flex gap-1 mt-4 overflow-hidden">
                  {board.steps.slice(0, 5).map((step) => (
                    <div
                      key={step.id}
                      className="h-2 flex-1 rounded-full opacity-70"
                      style={{ backgroundColor: step.color || '#6366f1' }}
                      title={step.name}
                    />
                  ))}
                  {board.steps.length > 5 && (
                    <div className="h-2 flex-1 rounded-full bg-gray-500/30" title={`+${board.steps.length - 5} mais`} />
                  )}
                </div>
              )}

              {!board.active && (
                <div className="mt-4">
                  <span className="px-2 py-1 rounded text-xs bg-gray-500/20 text-gray-400">
                    Inativo
                  </span>
                </div>
              )}
            </Card>
          ))}
        </div>
      ) : (
        <Card className="p-12 bg-brand-smoke border-brand-gold/20 text-center">
          <LayoutGrid className="w-16 h-16 text-brand-gold/50 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">Nenhum quadro encontrado</h3>
          <p className="text-brand-ivory/60 mb-6">
            Crie seu primeiro quadro para visualizar e gerenciar clientes em cada etapa do fluxo
          </p>
          <Button onClick={() => navigate('/app/quadros/novo')}>
            <Plus className="w-5 h-5 mr-2" />
            Criar Primeiro Quadro
          </Button>
        </Card>
      )}
    </div>
  );
}
